/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { MouseEvent, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { useTableStoreByRoute } from "@/stores/useTableStore";

import {
  DeleteFilledIcon,
  EditIcon,
  MoreVertIcon
} from "@dotnet/telematics-design-system";

import { UserDto } from "../../../../../Pages/Auth/types";
import {
  DRIVERS_ACTIONS,
  ICON_DEFAULT_STYLE,
  MenuAnchorEl,
  ROUTES,
  TABLE_NAMES,
  tableNameParams,
  VEHICLES_ACTIONS
} from "../../../../../shared";
import { refreshTableAndUpdateDetails } from "../../../../../shared/functions/functions";
import useResponse from "../../../../../shared/hooks/useResponse";
import Button from "../../../../Button/Button";
import useTableItemDetails from "../../../../DetailsSection/hooks/useTableItemDetails";
import ListChoice from "../../../../ListChoice/ListChoice";
import Menu from "../../../../Menu/Menu";
import { useDialog } from "../../../../PageHeader/actions/hooks/useDialog";
import Tooltip from "../../../../Tooltip/Tooltip";
import { useStyles } from "../../../styles";
import { ScheduleDataDto } from "../../../types";
import ConfirmDeleteDialog from "../../ConfirmDeleteDialog/ConfirmDeleteDialog";
import EditScheduleDialog from "../../EditScheduleDialog/EditScheduleDialog";

interface ScheduleCardActionsProps {
  resourceName: TABLE_NAMES;
  scheduleName: string;
  scheduleId: string | null;
  scheduleData: ScheduleDataDto | null;
  getSchedulesData: () => Promise<void>;
  user: UserDto | null;
}

const ScheduleCardActions = ({
  resourceName,
  scheduleName,
  scheduleId,
  scheduleData,
  getSchedulesData,
  user
}: ScheduleCardActionsProps) => {
  const { t } = useTranslation();
  const { isDialogOpen, openDialog, closeDialog } = useDialog();
  const location = useLocation();

  const {
    detailsData,
    params: { contains }
  } = useTableStoreByRoute(location.pathname as ROUTES);

  const { getTableItemDetails } = useTableItemDetails();
  const { handleResponse } = useResponse();

  const [anchorEl, setAnchorEl] = useState<MenuAnchorEl>(null);

  const { scheduleCardButtons } = useStyles();

  const resourceParams = tableNameParams[resourceName];

  const { actionDelete, actionEdit } = resourceParams;

  const isGlobalSchedule = Boolean(scheduleData?.global) || !scheduleData;

  const handleActionButton = (
    buttonName: VEHICLES_ACTIONS | DRIVERS_ACTIONS
  ) => {
    openDialog?.(buttonName);
    handleCloseMenu();
  };

  const handleContentRefresh = async (
    dialogName: VEHICLES_ACTIONS | DRIVERS_ACTIONS,
    successMessage: string,
    leaveRowsChecked?: boolean
  ) => {
    const currentTableDetailsSchedules = detailsData?.schedules.map(
      (schedule: any) => schedule.scheduleId
    );

    const itemHasOpenedDetails =
      currentTableDetailsSchedules?.includes(scheduleId);

    await refreshTableAndUpdateDetails({
      valueInSearchField: contains,
      resourceId: detailsData?.id,
      successMessage,
      itemHasOpenedDetails,
      getTableItemDetails,
      handleResponse,
      leaveRowsChecked
    });

    await getSchedulesData();

    closeDialog(dialogName);
  };

  const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => setAnchorEl(null);

  const editScheduleData = scheduleData && {
    ...scheduleData,
    ...(scheduleData?.nextDate && { startDate: scheduleData?.nextDate })
  };

  return (
    <>
      <div css={css(scheduleCardButtons)}>
        {isGlobalSchedule && !scheduleId && (
          <Tooltip
            title={t("Button##edit")}
            size="medium"
            placement="top"
            hasArrow
          >
            <Button
              data-testid="edit-schedule-button"
              variant="iconOnly"
              size="tiny"
              color="white"
              onClick={() => handleActionButton(actionEdit)}
              icon={<EditIcon style={ICON_DEFAULT_STYLE} />}
            />
          </Tooltip>
        )}

        {scheduleId && (
          <Tooltip
            title={t("Button##actions")}
            size="medium"
            placement="top"
            hasArrow
          >
            <Button
              data-testid="schedule-actions-button"
              variant="iconOnly"
              size="tiny"
              color="white"
              onClick={handleOpenMenu}
              icon={<MoreVertIcon style={ICON_DEFAULT_STYLE} />}
            />
          </Tooltip>
        )}

        <Menu
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorEl={anchorEl}
          slotProps={{ list: { disableListWrap: true } }}
          css={css({
            "& .MuiMenu-paper": {
              width: "179px"
            }
          })}
        >
          <ListChoice
            data-testid="edit-schedule-option"
            title={t("Button##edit")}
            onClick={() => handleActionButton(actionEdit)}
            iconLeft={<EditIcon style={ICON_DEFAULT_STYLE} />}
            alignIconCenter
          />
          {!isGlobalSchedule && (
            <ListChoice
              data-testid="delete-schedule-option"
              title={t("Button##delete")}
              onClick={() => handleActionButton(actionDelete)}
              iconLeft={<DeleteFilledIcon style={ICON_DEFAULT_STYLE} />}
              alignIconCenter
            />
          )}
        </Menu>
      </div>
      {scheduleId && (
        <ConfirmDeleteDialog
          resourceParams={resourceParams}
          scheduleId={scheduleId}
          scheduleName={scheduleName}
          isOpen={isDialogOpen[actionDelete]}
          onClose={closeDialog}
          handleContentRefresh={handleContentRefresh}
        />
      )}

      {isDialogOpen[actionEdit] && (
        <EditScheduleDialog
          resourceParams={resourceParams}
          isOpen={isDialogOpen[actionEdit]}
          isGlobalSchedule={isGlobalSchedule}
          scheduleData={editScheduleData as ScheduleDataDto}
          onClose={closeDialog}
          handleContentRefresh={handleContentRefresh}
          user={user}
        />
      )}
    </>
  );
};

export default ScheduleCardActions;
